import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "../Layout/PageHeader";
import "react-vis/dist/style.css";
import SubAccountList from "./SubAccountList";
import SubAccountFilter from "./SubAccountFilter";
import axios from "axios";
import {
  Row,
  Col,
  Collapse,
  Pagination,
  Button,
  Modal,
  Space,
  message,
} from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { difference, intersection } from "lodash";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const describeChanges = (added, removed) =>
  [
    added.length > 0 && `Adds ${added.join(", ")}`,
    removed.length > 0 && `Removes ${removed.join(", ")}`,
  ]
    .filter(Boolean)
    .join(". ");

const inheritChanges = (subAccount, masterFeatures) => {
  const features = subAccount.betaFeatures || [];
  const lost = difference(subAccount.inheritedFeatures || [], masterFeatures);

  return describeChanges(
    difference(masterFeatures, features),
    intersection(features, lost),
  );
};

const removeInheritanceChanges = (subAccount) =>
  describeChanges(
    [],
    intersection(subAccount.inheritedFeatures || [], subAccount.betaFeatures || []),
  );

const FeatureChanges = ({ subAccounts, changesOf, followingChange }) => (
  <ul style={{ paddingLeft: 16 }}>
    {subAccounts.map((subAccount) => (
      <li key={subAccount.id}>
        <strong>{subAccount.email}</strong>
        <br />
        {changesOf(subAccount) || "No feature changes"}
        <br />
        {followingChange}
      </li>
    ))}
  </ul>
);

const SubAccountDetails = ({ userId, masterFeatures }) => {
  const { id: routeId } = useParams();
  const masterAccountId = userId || routeId;
  const { permissions } = usePermissions();
  const canInherit =
    !!masterFeatures &&
    permissions?.includes(PERMISSIONS.UPDATE_BETA_FEATURES);
  const [filters, setFilters] = useState({});
  const [userList, setUserList] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const masterFeaturesKey = [...(masterFeatures || [])].sort().join(",");
  const fetchUser = useCallback(async () => {
    setIsLoading(true);
    const params = {
      masterAccountId: masterAccountId,
      ...(filters.startDate !== null && {
        startDate: filters.startDate?.startOf("day")?.toISOString(),
      }),
      ...(filters.endDate !== null && {
        endDate: filters.endDate?.endOf("day")?.toISOString(),
      }),
      ...(filters.search && {
        searchField: filters.searchField,
        search: filters.search,
      }),
      limit: pagination.limit,
      page: pagination.currentPage,
    };

    try {
      const res = await axios.get(`/api/users`, { params });

      setUserList(res.data.data);
      setSelectedRows([]);
      setPagination(res.data.pagination);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  }, [masterAccountId, pagination.currentPage, pagination.limit, filters]);
  useEffect(() => {
    fetchUser();
  }, [pagination.currentPage, pagination.limit, fetchUser, masterFeaturesKey]);

  const confirmFeatureChange = ({
    title,
    okText,
    action,
    changesOf,
    followingChange,
  }) => {
    const subAccounts = (userList || []).filter((subAccount) =>
      selectedRows.includes(subAccount.id),
    );

    Modal.confirm({
      title,
      okText,
      content: (
        <FeatureChanges
          subAccounts={subAccounts}
          changesOf={changesOf}
          followingChange={followingChange}
        />
      ),
      onOk: async () => {
        try {
          await axios.post(`/api/user/${masterAccountId}/sub-accounts/${action}`, {
            subAccountIds: selectedRows,
          });
          message.success("Updated");
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        } finally {
          await fetchUser();
        }
      },
    });
  };

  const inheritFeatures = () =>
    confirmFeatureChange({
      title: "Feature changes",
      okText: "Inherit Features",
      action: "inherit-features",
      changesOf: (subAccount) => inheritChanges(subAccount, masterFeatures),
      followingChange: "Will follow master",
    });

  const removeInheritance = () =>
    confirmFeatureChange({
      title: "Feature changes",
      okText: "Remove Inheritance",
      action: "remove-feature-inheritance",
      changesOf: removeInheritanceChanges,
      followingChange: "Will stop following master",
    });

  return (
    <div>
      <PageHeader
        title="Sub Account Details"
        childRoutes={[
          { path: "/sub-account", breadcrumbName: "Master Account" },
        ]}
      />

      <div className="table mt-4">
        {/* <Tabs defaultActiveKey="1" animated={true}> */}
          {/* <Tabs.TabPane tab="All User" key="all-transactions"> */}
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              <Collapse.Panel header="Filters" key="filters">
                <SubAccountFilter
                  filters={filters}
                  onFiltersChange={(filters) => setFilters(filters)}
                  tabName="allUser"
                />
              </Collapse.Panel>
            </Collapse>
            <br />
            <Row justify="space-between">
              <Col>
                {canInherit && (
                  <Space>
                    <Button
                      type="primary"
                      disabled={!selectedRows.length}
                      onClick={inheritFeatures}
                    >
                      Inherit Features
                    </Button>
                    <Button
                      disabled={!selectedRows.length}
                      onClick={removeInheritance}
                    >
                      Remove Inheritance
                    </Button>
                  </Space>
                )}
              </Col>
              <Col>
                <Pagination
                  total={pagination?.totalSize}
                  pageSize={pagination?.limit}
                  onChange={(currentPage, limit) => {
                    setPagination({ ...pagination, limit, currentPage });
                  }}
                  current={pagination.currentPage}
                />
              </Col>
            </Row>
            <SubAccountList
              masterAccount={userList}
              selectedRowKeys={selectedRows}
              onSelectRows={(selectedRowKeys) =>
                setSelectedRows(selectedRowKeys)
              }
              name="user"
              loading={isLoading}
            />

            <Row justify="end">
              <Col>
                <Pagination
                  total={pagination?.totalSize}
                  pageSize={pagination?.limit}
                  onChange={(currentPage, limit) => {
                    setPagination({ ...pagination, limit, currentPage });
                  }}
                  current={pagination.currentPage}
                />
              </Col>
            </Row>
          {/* </Tabs.TabPane> */}

          {/* <Tabs.TabPane tab="Topup" key="weightDispute">
            <AccountManagerTopup accountManagerId={id} />
          </Tabs.TabPane>
        </Tabs> */}
      </div>
    </div>
  );
};

export default SubAccountDetails;
